import Settings16 from '@carbon/icons-react/es/settings/16';
import View16 from '@carbon/icons-react/es/view/16';
import { Tooltip } from 'antd';
import React, { memo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import { CustomerPortalInviteMethod } from 'api/customer-portal/settings-api';
import { ProjectId } from 'api/models';
import { Project } from 'api/project';
import { Button } from 'components/atoms/button';
import { HideForCustomer } from 'components/atoms/user-type-utils';
import { AvatarInviteTrigger } from 'components/organisms/projects/global-invite/avatar-invite-trigger';
import { useGetCustomerPortalSettings } from 'hooks/customer-portal/use-customer-portal-hooks';
import { ProjectResource } from 'hooks/rbac/useWithAccess';
import { HideIfNotAllowed } from 'hooks/rbac/withAccess';
import { getCustomerPreviewUrl, getProjectSettingsUrl } from 'utils/urls';
import { PlaceholderResolution } from '../placeholder-resolution';
import {
  CompanyLogo,
  HeaderContainer,
  RightMenuContainer,
  Title,
  TitleContainer,
} from './project-title.styles';

export const ProjectTitle = ({ project }: { project: Project }) => {
  const {
    projectId,
    projectName,
    customer: { companyName, companyLogoUrl },
  } = project;

  const { data: cpSettingsData } = useGetCustomerPortalSettings();

  return (
    <HeaderContainer>
      <TitleContainer>
        <CompanyLogo
          src={companyLogoUrl}
          name={companyName || projectName}
          size={32}
        />
        <Title data-cy="page.header.title">{projectName}</Title>
      </TitleContainer>

      <HideForCustomer>
        <RightMenuContainer size="small">
          <HideIfNotAllowed
            rule="PROJECT_MEMBER:CREATE"
            resourceData={ProjectResource(project)}
          >
            <PlaceholderResolution project={project} />
          </HideIfNotAllowed>
          <div>
            <HideIfNotAllowed
              rule="PROJECT_VIEW_AS_CUSTOMER:READ"
              resourceData={ProjectResource(project)}
            >
              <Tooltip
                title={
                  <FormattedMessage
                    id="projects.preview.menu.label"
                    values={{ company_name: companyName }}
                  />
                }
              >
                <Link
                  to={{
                    pathname: getCustomerPreviewUrl(
                      `/projects/${projectId}/plan`,
                    ),
                  }}
                  target="_blank"
                  data-cy="link.customer-preview"
                >
                  <Button type="text" icon={<View16 />} />
                </Link>
              </Tooltip>
            </HideIfNotAllowed>
            <Tooltip title={<FormattedMessage id="projects.settings" />}>
              <Link
                data-cy="project.settings"
                to={getProjectSettingsUrl(ProjectId(projectId))}
              >
                <Button type="text" icon={<Settings16 />} />
              </Link>
            </Tooltip>
          </div>
          <HideIfNotAllowed
            rule="PROJECT_MEMBER:CREATE"
            resourceData={ProjectResource(project)}
          >
            <AvatarInviteTrigger
              projectId={projectId}
              showButton
              dontShowCustomerUsers={
                cpSettingsData?.customerPortalSettings?.inviteMethod ===
                  CustomerPortalInviteMethod.EmailsFromCustomerDomainsAndAdditionalDomains &&
                cpSettingsData?.customerPortalSettings?.inviteMethodMeta
                  ?.userWithPermissionCanInviteCustomers === false
              }
            />
          </HideIfNotAllowed>
        </RightMenuContainer>
      </HideForCustomer>
    </HeaderContainer>
  );
};
export const MemoisedProjectTitle = memo(ProjectTitle);
